/**
 * Real-time topic subscription interfaces and utilities.
 * Defines granular topic naming conventions, subscription quotas,
 * and API payload contracts for WebSocket and REST endpoints.
 */

/**
 * Supported topic domain types.
 * Strictly lowercase database enumerations.
 */
export type TopicDomain =
  | "user"
  | "group"
  | "memory"
  | "media"
  | "workflow"
  | "share"
  | "event"
  | "message"
  | "tag"
  | "place"
  | "anchor";

/**
 * Maximum concurrent topic subscriptions allowed per user connection/session.
 */
export const MAX_TOPICS_PER_SESSION = 50;

/**
 * Validates whether a given topic string follows the standard format `domain:id`.
 * @param topic The topic string to test.
 * @returns True if valid, false otherwise.
 */
export function isValidTopic(topic: unknown): topic is string {
  if (typeof topic !== "string" || !topic.trim()) {
    return false;
  }
  const match = topic.match(/^(user|group|memory|media|workflow|share|event|message|tag|place|anchor):([a-zA-Z0-9_-]+)$/);
  return match !== null;
}

/**
 * Parses a topic string into its domain and unique identifier.
 * @param topic The topic string to parse.
 * @returns Object with domain and id, or null if invalid.
 */
export function parseTopic(topic: string): { domain: TopicDomain; id: string } | null {
  if (!isValidTopic(topic)) {
    return null;
  }
  const parts = topic.split(":");
  return {
    domain: parts[0] as TopicDomain,
    id: parts[1],
  };
}

/**
 * In-memory or persisted representation of a topic subscription.
 */
export interface ITopicSubscription {
  /** Topic identifier (e.g. "memory:m123", "user:u456") */
  topic: string;
  /** Subscribed user UID */
  userId: string;
  /** Epoch timestamp in milliseconds when subscription occurred */
  subscribedAt: number;
  /** Optional persistent flag for subscriptions stored in database */
  persistent?: boolean;
}

/**
 * Request payload for subscribing to one or more topics.
 */
export interface ISubscribeTopicRequest {
  /** Single topic or array of topics */
  topic?: string;
  topics?: string[];
  /** Whether the subscription should persist across reconnects */
  persistent?: boolean;
}

/**
 * Response payload for subscription requests.
 */
export interface ISubscribeTopicResponse {
  /** Whether operation succeeded */
  success: boolean;
  /** List of successfully subscribed topics */
  subscribed: string[];
  /** List of rejected topics with error reasons */
  rejected?: Array<{ topic: string; reason: string }>;
  /** Total active subscriptions for caller */
  activeCount: number;
  /** Maximum quota allowed */
  maxAllowed: number;
}

/**
 * Request payload for unsubscribing from topics.
 */
export interface IUnsubscribeTopicRequest {
  /** Single topic or array of topics to unsubscribe */
  topic?: string;
  topics?: string[];
  /** If true, unregisters all active topics (except user personal topic) */
  all?: boolean;
}

/**
 * Response payload for unsubscribe requests.
 */
export interface IUnsubscribeTopicResponse {
  success: boolean;
  unsubscribed: string[];
  activeCount: number;
}

/**
 * Response payload listing user's subscribed topics.
 */
export interface IMyTopicsResponse {
  userId: string;
  activeTopics: string[];
  persistentTopics: string[];
  activeCount: number;
  maxAllowed: number;
}
